/* import __COLOCATED_TEMPLATE__ from './rollout-percentage-selector.hbs'; */
/* RESPONSIBLE TEAM: team-phone */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import type { PopoverAPI } from '@intercom/pulse/template-registry';

interface Args {
  rolloutPercentage?: number;
  updateRolloutPercentage: (rolloutPercentage: number) => Promise<void>;
  isSaving: boolean;
  disabledMessage?: string;
  onPopoverChange?: (isOpen: boolean) => void;
}

interface Signature {
  Element: HTMLElement;
  Args: Args;
}

export default class RolloutPercentageSelector extends Component<Signature> {
  @service declare intl: IntlService;

  @tracked _rolloutPercentage = this.args.rolloutPercentage || 0;

  get rolloutPercentage() {
    return this._rolloutPercentage;
  }

  set rolloutPercentage(value: any) {
    let parsedValue = parseInt(value, 10);

    if (!parsedValue || parsedValue < 0) {
      this._rolloutPercentage = 0;
    } else if (parsedValue > 100) {
      this._rolloutPercentage = 100;
    } else {
      this._rolloutPercentage = parsedValue;
    }
  }

  get savingDisabled() {
    return (
      !this.isValid ||
      !!this.args.disabledMessage ||
      this.args.isSaving ||
      this.savedRolloutPercentage === this.rolloutPercentage
    );
  }

  get isOpenerDisabled() {
    return !!this.args.disabledMessage || this.args.isSaving;
  }

  get isValid() {
    return this.rolloutPercentage >= 0 && this.rolloutPercentage <= 100;
  }

  get savedRolloutPercentage() {
    return this.args.rolloutPercentage || 0;
  }

  get buttonType() {
    if (!this.savedRolloutPercentage) {
      return 'secondary-floating';
    }

    if (this.savedRolloutPercentage === 100) {
      return 'primary-live';
    }

    return 'secondary-live';
  }

  get buttonLabel() {
    if (!this.savedRolloutPercentage) {
      return this.intl.t(
        'operator.fin.setup.tabs.setup-and-go-live.phone.rollout-percentage-selector.set-fin-live',
      );
    }

    if (this.savedRolloutPercentage === 100) {
      return this.intl.t(
        'operator.fin.setup.tabs.setup-and-go-live.phone.rollout-percentage-selector.live-to-all',
      );
    }

    return this.intl.t(
      'operator.fin.setup.tabs.setup-and-go-live.phone.rollout-percentage-selector.partially-live',
      {
        percentage: this.savedRolloutPercentage,
      },
    );
  }

  @action
  openPopover(popover: PopoverAPI) {
    this._rolloutPercentage = this.savedRolloutPercentage;
    if (popover.isVisible) {
      popover.hide();
      this.args.onPopoverChange?.(false);
    } else {
      popover.show();
      this.args.onPopoverChange?.(true);
    }
  }

  @action
  async updateRolloutPercentage(popover: PopoverAPI) {
    if (!this.isValid || this.args.isSaving) {
      return;
    }

    popover.hide();
    await this.args.updateRolloutPercentage(this.rolloutPercentage);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Operator::Fin::Setup::Components::RolloutPercentageSelector': typeof RolloutPercentageSelector;
  }
}
